/**
 * Current-user state for the whole app.
 *
 * Deliberately a React context rather than reading localStorage ad hoc: in the
 * old app every hook did `getSelectedClient()?.clientId` synchronously in its
 * body, so nothing re-rendered when it changed. Here the user is real state —
 * a login, logout or role change re-renders every consumer.
 *
 * Sep-3 (v1): wall-clock expiry via lastActivity + sessionStart in localStorage.
 * Sep-3 (v3): restore uses ABSOLUTE-only check + calls /auth/logout best-effort
 *             before clearing state.
 * Sep-3 (v4): REMOVED the pagehide beacon that shipped in v2. `pagehide` fires
 *             on page refresh (F5), not just tab close — every refresh was
 *             logging the user out server-side via /auth/logout, which cleared
 *             the backend session_id; then the reloaded page's /auth/me hit
 *             a session-id mismatch and got 401 "Session is no longer valid".
 *             There is no reliable browser API to distinguish "user refreshed"
 *             from "user closed the tab" at pagehide time, so the beacon
 *             approach was fundamentally unsafe. The "orphaned session_id
 *             after tab close" problem must be solved on the backend instead
 *             (session TTL + freshness-based single-session check — see the
 *             notes shipped with this fix).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getToken, setToken, clearToken,
  setSessionStart, setLastActivity, clearSessionStart, clearLastActivity,
  ApiError, onUnauthorized,
} from "@shared/api";
import { isSessionExpired, isAbsoluteSessionExpired } from "./IdleSessionWatcher";
import * as authApi from "./api/auth.api";
import type { AuthUser } from "./api/auth.schemas";

interface AuthContextValue {
  user: AuthUser | null;
  /** True while we're restoring a session from a stored token on first load. */
  initialising: boolean;
  signIn: (email: string, password: string, recaptchaToken?: string) => Promise<AuthUser>;
  signOut: () => void;
  /** Re-fetch /auth/me — used after a module_disabled 403 to refresh state. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserRaw] = useState<AuthUser | null>(null);
  const [initialising, setInitialising] = useState(true);

  /** Wraps setUser to also persist org/group names to localStorage so the
   *  TopNav can render the org crumb instantly on reload without waiting for
   *  /auth/me to resolve. Cleared on logout via the specta.* sweep. */
  const setUser = useCallback((u: AuthUser | null) => {
    setUserRaw(u);
    try {
      if (u?.org_name) localStorage.setItem("specta.org_name", u.org_name);
      if (u?.group_name) localStorage.setItem("specta.group_name", u.group_name);
    } catch { /* storage unavailable */ }
  }, []);

  // Restore the session on load: if we hold a token, ask the server who we are.
  // Uses ABSOLUTE-only check (isAbsoluteSessionExpired) so a stale-input
  // lastActivity doesn't kick an active user out on refresh. Absolute cap
  // (SESSION_MAX_MS) still catches overnight sleep. Before clearing, we call
  // /auth/logout best-effort so the DB session_id doesn't get orphaned.
  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      if (!getToken()) {
        setInitialising(false);
        return;
      }
      // Absolute-cap check only — refresh should always work for a session
      // within the cap. The 15-min idle check is enforced by the in-app
      // useIdleTimeout while the tab is running, and by the wake handler
      // (below) when the user tabs back in.
      if (isAbsoluteSessionExpired()) {
        try {
          await authApi.logout();
        } catch {
          /* best-effort backend cleanup; ignore failures */
        }
        clearToken();
        clearLastActivity();
        clearSessionStart();
        if (!cancelled) setInitialising(false);
        return;
      }
      try {
        const me = await authApi.fetchMe();
        if (!cancelled) setUser(me);
      } catch (e) {
        // An expired/invalid token is the normal path here, not an error.
        // We do NOT call /auth/logout here — server said this token is
        // invalid, so a logout with it would 401 too.
        if (e instanceof ApiError && e.isUnauthorized) {
          clearToken();
          clearLastActivity();
          clearSessionStart();
        }
      } finally {
        if (!cancelled) setInitialising(false);
      }
    };
    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  // A 401 anywhere (expired token, single-session eviction, revoked access)
  // ends the session in the UI: the client already cleared the token, so we
  // just null the user and RequireAuth redirects to /login. Setting null when
  // already null is a no-op React skips, so concurrent 401s don't thrash.
  useEffect(() => {
    onUnauthorized(() => {
      clearLastActivity();
      clearSessionStart();
      setUser(null);
    });
    return () => onUnauthorized(null);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string, recaptchaToken?: string) => {
      const res = await authApi.login(email, password, recaptchaToken);
      setToken(res.access_token);
      // Wall-clock anchors so isAbsoluteSessionExpired / isSessionExpired
      // have something to compare against on the next mount.
      const now = Date.now();
      setSessionStart(now);
      setLastActivity(now);
      setUser(res.user);
      return res.user;
    },
    [],
  );

  // Re-fetch the user so module/permission changes made mid-session take effect
  // without a full reload (handoff §3, §7). Silent — a failure here shouldn't
  // interrupt whatever the user was doing.
  const refreshUser = useCallback(async () => {
    if (!getToken()) return;
    try {
      setUser(await authApi.fetchMe());
    } catch {
      /* keep the current user; the 401 path handles an expired session */
    }
  }, []);

  const signOut = useCallback(async () => {
    // Await the logout so the token is still attached when the request fires.
    // The server now enforces single-session, so skipping this strands the
    // session and blocks the next login.
    try {
      await authApi.logout();
    } catch {
      // Still log out locally even if the server call fails.
    } finally {
      clearToken();
      clearLastActivity();
      clearSessionStart();
      setUser(null);

      // Clear user-scoped data so a re-login as a different user doesn't see
      // the previous user's selected agent, cached queries, or FIM dismissed
      // warnings. Theme + sidebar preferences are UI-only and survive logout.
      try {
        const preserve = ["specta.theme", "specta.sidebar.collapsed"];
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith("specta.") && !preserve.includes(key)) {
            toRemove.push(key);
          }
        }
        toRemove.forEach((k) => localStorage.removeItem(k));
      } catch {
        // Storage unavailable — proceed with logout anyway.
      }

      // Wipe the React Query cache so stale data from the previous user's org
      // doesn't flash on the next login.
      try {
        const { queryClient } = await import("@app/providers");
        queryClient.clear();
      } catch {
        // Provider not available — proceed.
      }
    }
  }, []);

  // Wake-up detection. `visibilitychange` fires when a tab becomes visible
  // after the OS wakes from sleep or the user tabs back in; `focus` covers
  // window-manager focus swaps and some browser edge cases that don't fire
  // `visibilitychange` (older Safari especially). Both re-check wall-clock
  // expiry via the FULL isSessionExpired (idle + absolute), and if the
  // session is dead, sign out — signOut calls /auth/logout so backend
  // session_id gets cleared. Only wired while logged in.
  useEffect(() => {
    if (user === null) return;
    const check = () => {
      if (isSessionExpired()) {
        void signOut();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, signOut]);

  // NOTE — intentionally NO pagehide beacon.
  //
  // A pagehide → /auth/logout beacon was tried twice and reverted twice:
  // `pagehide` fires on refresh (F5, Ctrl/Cmd-R, the reload button) and on
  // the dev server's HMR full-reload, not just on tab close — and there is
  // no reliable browser signal at pagehide time to tell a refresh from a
  // close. Any version that calls the server here eventually clears the
  // backend session_id on a refresh the guard didn't catch, and the
  // reloaded page's /auth/me then 401s with "Session is no longer valid",
  // bouncing the user to login. The stale-session-after-close problem is
  // covered by the server's 15-minute TTL and must be fully solved
  // backend-side (freshness-based single-session). Do not re-add a beacon
  // here without a backend change that makes a stray logout harmless.

  const value = useMemo(
    () => ({ user, initialising, signIn, signOut, refreshUser }),
    [user, initialising, signIn, signOut, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
